// src/App.js - Redesigned for "What Would You Do?" quiz
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Eagerly loaded — needed on first paint
import LandingPage from './pages/LandingPage';
import QuizFlowPage from './pages/QuizFlowPage';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import NetworkErrorBoundary from './components/NetworkErrorBoundary';
import InAppBrowserWarning from './components/InAppBrowserWarning';
import { ResultsProvider } from './contexts/ResultsContext';
import { Analytics } from '@vercel/analytics/react';

// Lazy loaded — only fetched when navigated to
const FreeResultsPage = lazy(() => import('./pages/FreeResultsPage'));
const DeepDivePage = lazy(() => import('./pages/DeepDivePage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const PredictResultPage = lazy(() => import('./pages/PredictResultPage'));
const CoupleReportPage = lazy(() => import('./pages/CoupleReportPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SignUp = lazy(() => import('./components/Auth/SignUp'));
const Login = lazy(() => import('./components/Auth/Login'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const PrivateRoute = lazy(() => import('./components/Auth/PrivateRoute'));
const PaidContentRoute = lazy(
  () => import('./components/Auth/PaidContentRoute')
);
const Profile = lazy(() => import('./components/User/Profile'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-neutral-300 border-t-accent rounded-full animate-spin"></div>
      <p className="text-sm text-neutral-500">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <>
      <SentryErrorBoundary>
        <ErrorBoundary>
          <NetworkErrorBoundary>
            <ResultsProvider>
              <Router>
                {/* In-app browser warning (now a dismissible banner) */}
                <InAppBrowserWarning />

                {/* Navbar will be visible on all pages */}
                <Navbar />

                {/* Add padding to account for fixed navbar */}
                <div className="pt-16">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Core quiz flow — no auth required */}
                      <Route
                        path="/"
                        element={
                          <ErrorBoundary>
                            <LandingPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/quiz"
                        element={
                          <ErrorBoundary>
                            <QuizFlowPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/results"
                        element={
                          <ErrorBoundary>
                            <FreeResultsPage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Share page — public result viewer */}
                      <Route
                        path="/r/:shareId"
                        element={
                          <ErrorBoundary>
                            <SharePage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/share/:shareId"
                        element={
                          <ErrorBoundary>
                            <SharePage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Comparison — no auth required */}
                      <Route
                        path="/compare/:compareId?"
                        element={
                          <ErrorBoundary>
                            <ComparePage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Predict-my-answers result — no auth required */}
                      <Route
                        path="/predict-result"
                        element={
                          <ErrorBoundary>
                            <PredictResultPage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Deep dive — requires auth + payment */}
                      <Route
                        path="/deep-dive"
                        element={
                          <ErrorBoundary>
                            <PrivateRoute>
                              <PaidContentRoute>
                                <DeepDivePage />
                              </PaidContentRoute>
                            </PrivateRoute>
                          </ErrorBoundary>
                        }
                      />

                      {/* Couple Report — CoupleReportPage renders its own
                          auth + entitlement gates so guests hitting a magic
                          sign-in link still see the right prompt. */}
                      <Route
                        path="/couple-report/:compareId?"
                        element={
                          <ErrorBoundary>
                            <CoupleReportPage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Auth routes */}
                      <Route
                        path="/signup"
                        element={
                          <ErrorBoundary>
                            <SignUp />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <ErrorBoundary>
                            <Login />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/forgot-password"
                        element={
                          <ErrorBoundary>
                            <ForgotPassword />
                          </ErrorBoundary>
                        }
                      />

                      {/* User profile — requires auth */}
                      <Route
                        path="/profile"
                        element={
                          <ErrorBoundary>
                            <PrivateRoute>
                              <Profile />
                            </PrivateRoute>
                          </ErrorBoundary>
                        }
                      />

                      {/* Payment routes */}
                      <Route
                        path="/payment-success"
                        element={
                          <ErrorBoundary>
                            <PaymentSuccessPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/payment-cancel"
                        element={
                          <ErrorBoundary>
                            <PaymentCancelPage />
                          </ErrorBoundary>
                        }
                      />

                      {/* Info pages */}
                      <Route
                        path="/about"
                        element={
                          <ErrorBoundary>
                            <AboutPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/faq"
                        element={
                          <ErrorBoundary>
                            <FAQPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/legal"
                        element={
                          <ErrorBoundary>
                            <LegalPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/privacy"
                        element={
                          <ErrorBoundary>
                            <PrivacyPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <ErrorBoundary>
                            <ContactPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/support"
                        element={
                          <ErrorBoundary>
                            <SupportPage />
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/cookies"
                        element={
                          <ErrorBoundary>
                            <CookiePage />
                          </ErrorBoundary>
                        }
                      />
                    </Routes>
                  </Suspense>
                </div>
              </Router>
            </ResultsProvider>
          </NetworkErrorBoundary>
        </ErrorBoundary>
      </SentryErrorBoundary>
      <Analytics />
    </>
  );
}

export default App;
